import {
  createGeneralDraft,
  GENERAL_KINDS,
  type GeneralDraft,
} from "@/features/general/strategy";

export const PUBLIC_STRATEGY_PATH = "/learn";
export const PUBLIC_KINDS = GENERAL_KINDS.filter((kind) => kind !== "custom");
export function publicTheoryPath(kind: string) {
  return `/learn/strategies/${kind.replaceAll("_", "-")}`;
}
export function publicExplorePath(kind: string) {
  return `${publicTheoryPath(kind)}/explore`;
}
export const PUBLIC_STRATEGY_IMPORT_PATH = "/workspace/import/strategy";
export const PUBLIC_STRATEGY_LOGIN_PATH = `/login?returnTo=${encodeURIComponent(PUBLIC_STRATEGY_IMPORT_PATH)}`;
const STORAGE_KEY = "abg.public-strategy.pending.v1";

export function ironCondorExample(
  name = "Iron condor · teaching example",
): GeneralDraft {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + 30);
  date.setUTCDate(date.getUTCDate() + ((5 - date.getUTCDay() + 7) % 7));
  const expiry = date.toISOString().slice(0, 10);
  return {
    id: "public-iron-condor",
    name,
    structureKind: "iron_condor",
    referencePrice: 500,
    quantity: 1,
    enabled: true,
    color: "#d5f98b",
    legs: [
      {
        id: "long-put",
        type: "put",
        side: "long",
        strike: 480,
        premium: 1,
        quantity: 1,
        expiry,
      },
      {
        id: "short-put",
        type: "put",
        side: "short",
        strike: 490,
        premium: 3,
        quantity: 1,
        expiry,
      },
      {
        id: "short-call",
        type: "call",
        side: "short",
        strike: 510,
        premium: 3,
        quantity: 1,
        expiry,
      },
      {
        id: "long-call",
        type: "call",
        side: "long",
        strike: 520,
        premium: 1,
        quantity: 1,
        expiry,
      },
    ],
  };
}

// This is a temporary, tab-scoped handoff, never a saved strategy. Durable
// records go through the authenticated workspace provider into Postgres.
export function stagePublicStrategy(draft: GeneralDraft): void {
  clearPublicFavorite();
  sessionStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      version: 1,
      createdAt: Date.now(),
      draft: { ...draft, id: crypto.randomUUID() },
    }),
  );
}

export function readPublicStrategy(): GeneralDraft | null {
  try {
    return parsePublicStrategy(sessionStorage.getItem(STORAGE_KEY) ?? "null");
  } catch {
    return null;
  }
}

function parsePublicStrategy(serialized: string): GeneralDraft | null {
  try {
    const value = JSON.parse(serialized);
    const draft = value?.draft;
    if (
      value?.version !== 1 ||
      !Number.isFinite(value.createdAt) ||
      Date.now() - value.createdAt > 24 * 60 * 60 * 1000 ||
      value.createdAt > Date.now() ||
      !draft ||
      typeof draft.id !== "string" ||
      typeof draft.name !== "string" ||
      !draft.name.trim() ||
      draft.name.length > 60 ||
      !Number.isInteger(draft.quantity) ||
      draft.quantity < 1 ||
      draft.quantity > 100 ||
      draft.referencePrice !== 500 ||
      !PUBLIC_KINDS.includes(draft.structureKind) ||
      !Array.isArray(draft.legs) ||
      draft.legs.length < 1 ||
      draft.legs.length > 8 ||
      new Set(draft.legs.map((leg: { id: string }) => leg.id)).size !==
        draft.legs.length ||
      draft.legs.some(
        (leg: Record<string, unknown>) =>
          typeof leg.id !== "string" ||
          !["call", "put"].includes(String(leg.type)) ||
          !["long", "short"].includes(String(leg.side)) ||
          typeof leg.strike !== "number" ||
          !Number.isFinite(leg.strike) ||
          leg.strike <= 0 ||
          typeof leg.quantity !== "number" ||
          !Number.isInteger(leg.quantity) ||
          leg.quantity < 1 ||
          leg.quantity > 100 ||
          typeof leg.premium !== "number" ||
          !Number.isFinite(leg.premium) ||
          leg.premium < 0 ||
          typeof leg.expiry !== "string" ||
          !/^\d{4}-\d{2}-\d{2}$/.test(leg.expiry) ||
          !Number.isFinite(Date.parse(leg.expiry)) ||
          new Date(leg.expiry).toISOString().slice(0, 10) !== leg.expiry,
      ) ||
      new Set(draft.legs.map((leg: { expiry: string }) => leg.expiry)).size !==
        1
    )
      return null;
    // Never accept an injected source layer from browser storage.
    return { ...draft, source: undefined, enabled: true, color: "#d5f98b" };
  } catch {
    return null;
  }
}

export function clearPublicStrategy(): void {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function publicStrategyExample(
  kind: import("@/types/strategy").StrategyLayerKind,
  name: string,
): GeneralDraft {
  if (kind === "iron_condor") return ironCondorExample(name);
  const expiry = ironCondorExample().legs[0].expiry;
  const draft = createGeneralDraft(kind, name, 500, 1, expiry);
  return {
    ...draft,
    referencePrice: 500,
    legs: draft.legs.map((leg) => ({
      ...leg,
      // Illustrative premiums with intrinsic value plus time value, never market quotes.
      premium:
        Math.max(0, leg.type === "call" ? 500 - leg.strike : leg.strike - 500) +
        Math.max(0.5, 5 - Math.abs(leg.strike - 500) * 0.15),
    })),
  };
}
const FAVORITE_KEY = "abg.public-strategy.favorite.v1";
export function stagePublicFavorite(kind: string) {
  clearPublicStrategy();
  sessionStorage.setItem(
    FAVORITE_KEY,
    JSON.stringify({ kind, createdAt: Date.now() }),
  );
}
export function readPublicFavorite(): string | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(FAVORITE_KEY) ?? "null");
    return value &&
      PUBLIC_KINDS.includes(value.kind) &&
      Number.isFinite(value.createdAt) &&
      Date.now() >= value.createdAt &&
      Date.now() - value.createdAt < 86400000
      ? value.kind
      : null;
  } catch {
    return null;
  }
}
export function clearPublicFavorite() {
  sessionStorage.removeItem(FAVORITE_KEY);
}

// Only numeric example inputs travel in the fragment. Names, account IDs and
// source layers are deliberately excluded; nothing is written to the database.
export function publicScenarioHash(draft: GeneralDraft): string {
  const scenario = {
    v: 1,
    kind: draft.structureKind,
    quantity: draft.quantity,
    legs: draft.legs.map(
      ({ type, side, strike, premium, quantity, expiry }) => ({
        type,
        side,
        strike,
        premium,
        quantity,
        expiry,
      }),
    ),
  };
  return `#scenario=${encodeURIComponent(JSON.stringify(scenario))}`;
}

export function readPublicScenario(
  hash: string,
  kind: string,
  name: string,
): GeneralDraft | null {
  try {
    if (hash.length > 6000 || !hash.startsWith("#scenario=")) return null;
    const value = JSON.parse(
      decodeURIComponent(hash.slice("#scenario=".length)),
    );
    if (value?.v !== 1 || value.kind !== kind || !Array.isArray(value.legs))
      return null;
    return parsePublicStrategy(
      JSON.stringify({
        version: 1,
        createdAt: Date.now(),
        draft: {
          id: "public-shared-example",
          name,
          structureKind: kind,
          referencePrice: 500,
          quantity: value.quantity,
          legs: value.legs.map(
            (leg: GeneralDraft["legs"][number], index: number) => ({
              id: `shared-${index}`,
              type: leg.type,
              side: leg.side,
              strike: leg.strike,
              premium: leg.premium,
              quantity: leg.quantity,
              expiry: leg.expiry,
            }),
          ),
        },
      }),
    );
  } catch {
    return null;
  }
}
